import { useState } from "react";
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { trimStrings } from "@pulse-ui/formatting";
import { useAddOrgUserMutation, useUpdateOrgMemberRolesMutation } from "../services/roleManagementApi";
import { isAddUserFormValid } from "../utils/validators";
import { IAddUser } from "../RoleManagement.types";

const EMPTY_FORM: IAddUser = { firstname: "", lastname: "", email: "" };

export const useAddUserModal = (orgId: string | undefined) => {
    const { showSuccess, showError } = useAlerts();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState<IAddUser>(EMPTY_FORM);

    const [addOrgUser] = useAddOrgUserMutation();
    const [updateUserRoles] = useUpdateOrgMemberRolesMutation();

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!orgId || !isAddUserFormValid(form)) return;

        try {
            const result = await addOrgUser({
                orgId,
                data: trimStrings({ ...form }) as IAddUser,
            }).unwrap();

            // Set default viewer role for the new user
            if (result?.org_user_id) {
                await updateUserRoles({
                    orgUserId: result.org_user_id,
                    roles: ["viewer"],
                }).unwrap();
            }

            showSuccess("User successfully created.");
            setForm(EMPTY_FORM);
            setOpen(false);
        } catch (err) {
            showError(`Failed to add user: ${getErrorMessage(err)}`);
        }
    };

    return {
        open,
        addUserForm: form,
        disableSaveButton: !isAddUserFormValid(form),
        handleAddUserFormChange: handleFormChange,
        handlePostAddUser: handleSubmit,
        openModal: () => setOpen(true),
        closeAddUserModal: () => {
            setForm(EMPTY_FORM);
            setOpen(false);
        },
    };
};
